/*
 * KB Type definitions and meta-information
 * */

/*
 * Base for all Kb Type Definitions
 * */
import { DEFAULT_PUBLISHER_LLM_MODEL_NAME, DEFAULT_MAX_KB_SEARCH_DOCUMENTS, DEFAULT_SEMANTIC_ANALYZER_MODEL } from '@enegelai/core';

export class KbTypeDef {
  constructor() {
    this.type = '';
    this.category = '';
    this.title = '';
    this.description = '';
    this.defaultName = '';
    this.icon = 'lightbulb-on-20';
    this.helpUrl = '';
    this.props = [];
  }
}

export const emptyKbType = new KbTypeDef();

// Common Kb props, applicable for all knowledge bases
export const commonKbProps = [
  // TODO
];

/*
 * Kb Type Definition: Standard Knowledge Base
 * */
export class KbTypeStandard extends KbTypeDef {
  constructor() {
    super();
    this.type = 'standard';
    this.category = 'kb';
    this.title = 'Knowledge Base';
    this.description = 'Stores information used by Assistants';
    this.defaultName = 'New Knowledge Base';
    this.helpUrl = '/help/kb/standard';
    this.icon = 'database-outline';
    this.props = commonKbProps.concat([
      {
        name: 'webSourceEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Enable Web Data Source',
        default: false,
        description: 'Enable loading data from the Web sources, such as your website or online knowledge base.',
      },
      { name: 'sitemaps', advanced: false, type: 'text', title: 'Sitemaps', default: '', description: 'List of sitemap URLs to crawl. Separate sitemaps by new line.' },
      { name: 'urls', advanced: false, type: 'text', title: 'URLs', default: '', description: 'List of individual URLs to crawl. Separate URLs by new line.' },
      {
        name: 'zendeskHelpCenterSourceEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Enable Zendesk Help Center Data Source',
        default: false,
        description: 'Enable loading data from Zendesk Help Centers',
      },
      {
        name: 'zendeskHelpCentersSubdomains',
        advanced: false,
        type: 'string',
        title: 'Zendesk Help Centers Subdomains',
        default: '',
        description: 'Subdomains of Zendesk Help Centers to load data from. Separate subdomains by comma.',
      },
      {
        name: 'zendeskPrivateArticlesEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Load private articles',
        default: false,
        description: 'Enable loading private articles from Zendesk Help Centers',
      },
      {
        name: 'zendeskHelpCenterSourceRefreshInterval',
        advanced: true,
        type: 'select',
        title: 'Zendesk Help Center data refresh interval',
        description:
          'When this is enabled, Articles from your Zendesk Help Center will be refreshed in the Knowledge Base as often as selected. This will keep Knowledge base always in sync with the latest updates on your Zendesk Help Center.',
        options: ['Manual', '1 day', '1 week'],
        values: ['0', '86400', '604800'],
        default: '0',
      },
      {
        name: 'salesforceHelpCenterSourceEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Enable Salesforce Help Center Data Source',
        default: false,
        description: 'Enable loading data from Salesforce Help Centers',
      },
      {
        name: 'salesforceHelpCenterRecordTypes',
        advanced: false,
        type: 'string',
        title: 'Salesforce Help Center Record Types',
        default: '',
        description: 'Record Types of Salesforce Help Center to load data from.',
      },
      {
        name: 'salesforcePrivateArticlesEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Load private articles',
        default: false,
        description: 'Enable loading private articles from Salesforce Help Center',
      },
      {
        name: 'salesforceHelpCenterSourceRefreshInterval',
        advanced: true,
        type: 'select',
        title: 'Salesforce Help Center data refresh interval',
        description:
          'When this is enabled, Articles from your Salesforce Help Center will be refreshed in the Knowledge Base as often as selected. This will keep Knowledge base always in sync with the latest updates on your Salesforce Help Center.',
        options: ['Manual', '1 day', '1 week'],
        values: ['0', '86400', '604800'],
        default: '0',
      },
      {
        name: 'shopifyHelpCenterSourceEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Enable Shopify Help Center Data Source',
        default: false,
        description: 'Enable loading data from Shopify Help Centers',
      },
      {
        name: 'shopifyHelpCenterRecordTypes',
        advanced: false,
        type: 'string',
        title: 'Shopify Help Center Record Types',
        default: '',
        description: 'Record Types of Shopify Help Center to load data from.',
      },
      {
        name: 'shopifyPrivateArticlesEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Load private articles',
        default: false,
        description: 'Enable loading private articles from Shopify Help Center',
      },
      {
        name: 'shopifyHelpCenterSourceRefreshInterval',
        advanced: true,
        type: 'select',
        title: 'Shopify Help Center data refresh interval',
        description:
          'When this is enabled, Articles from your Shopify Help Center will be refreshed in the Knowledge Base as often as selected. This will keep Knowledge base always in sync with the latest updates on your Shopify Help Center.',
        options: ['Manual', '1 day', '1 week'],
        values: ['0', '86400', '604800'],
        default: '0',
      },
      {
        name: 'kustomerHelpCenterSourceEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Enable Kustomer Help Center Data Source',
        default: false,
        description: 'Enable loading data from Kustomer Help Centers',
      },
      {
        name: 'kustomerPrivateArticlesEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Load private articles',
        default: false,
        description: 'Enable loading private articles from Kustomer Help Center',
      },
      {
        name: 'kustomerHelpCenterSourceRefreshInterval',
        advanced: true,
        type: 'select',
        title: 'Kustomer Help Center data refresh interval',
        description:
          'When this is enabled, Articles from your Kustomer Help Center will be refreshed in the Knowledge Base as often as selected. This will keep Knowledge base always in sync with the latest updates on your Kustomer Help Center.',
        options: ['Manual', '1 day', '1 week'],
        values: ['0', '86400', '604800'],
        default: '0',
      },
      {
        name: 'gorgiasHelpCenterSourceEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Enable Gorgias Help Center Data Source',
        default: false,
        description: 'Enable loading data from Gorgias Help Centers',
      },
      {
        name: 'gorgiasHelpCenterSourceRefreshInterval',
        advanced: true,
        type: 'select',
        title: 'Gorgias Help Center data refresh interval',
        description:
          'When this is enabled, Articles from your Gorgias Help Center will be refreshed in the Knowledge Base as often as selected. This will keep Knowledge base always in sync with the latest updates on your Gorgias Help Center.',
        options: ['Manual', '1 day', '1 week'],
        values: ['0', '86400', '604800'],
        default: '0',
      },
      {
        name: 'gorgiasHelpCentersSubdomains',
        advanced: false,
        type: 'string',
        title: 'Gorgias Help Centers Subdomains',
        default: '',
        description: 'Subdomains of Gorgias Help Centers to load data from. Separate subdomains by comma.',
      },
      {
        name: 'intercomHelpCenterSourceEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Enable Intercom Help Center Data Source',
        default: false,
        description: 'Enable loading data from Intercom Help Center',
      },
      {
        name: 'intercomHelpCenterSourceRefreshInterval',
        advanced: true,
        type: 'select',
        title: 'Intercom Help Center data refresh interval',
        description:
          'When this is enabled, Articles from your Intercom Help Center will be refreshed in the Knowledge Base as often as selected. This will keep Knowledge base always in sync with the latest updates on your Intercom Help Center.',
        options: ['Manual', '1 day', '1 week'],
        values: ['0', '86400', '604800'],
        default: '0',
      },
      {
        name: 'confluenceHelpCenterSourceEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Enable Confluence Data Source',
        default: false,
        description: 'Enable loading data from Confluence',
      },
      /*
      {
        name: 'confluenceSpaces',
        advanced: false,
        type: 'string',
        title: 'Confluence Spaces',
        default: '',
        description: 'Confluence Spaces to load data from. Separate spaces by comma.',
      },
      */
      {
        name: 'confluenceSpaceKeys',
        advanced: false,
        type: 'string',
        title: 'Confluence Space Keys',
        default: '',
        description: 'Confluence Space Keys to load data from. Separate space keys by comma.',
      },
      {
        name: 'confluenceLabels',
        advanced: false,
        type: 'string',
        title: 'Confluence Labels',
        default: '',
        description: 'Confluence Labels to filter pages. Separate labels by comma.',
      },
      {
        name: 'confluenceHelpCenterSourceRefreshInterval',
        advanced: true,
        type: 'select',
        title: 'Confluence data refresh interval',
        description:
          'When this is enabled, Articles from your Confluence will be refreshed in the Knowledge Base as often as selected. This will keep Knowledge base always in sync with the latest updates on your Confluence.',
        options: ['Manual', '1 day', '1 week'],
        values: ['0', '86400', '604800'],
        default: '0',
      },
      {
        name: 'guruHelpCenterSourceEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Enable Guru Help Center Data Source',
        default: false,
        description: 'Enable loading data from Guru Help Center',
      },
      {
        name: 'guruHelpCentersTags',
        advanced: false,
        type: 'string',
        title: 'Guru Help Centers tags',
        default: '',
        description: 'List of tags of used to filter loaded documents, comma-separated. Only matching documents will be loaded.',
      },
      {
        name: 'guruPrivateArticlesEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Load private articles',
        default: false,
        description: 'Enable loading private articles from Guru Help Centers',
      },
      {
        name: 'guruHelpCenterSourceRefreshInterval',
        advanced: true,
        type: 'select',
        title: 'Guru Help Center data refresh interval',
        description:
          'When this is enabled, Articles from your Guru Help Center will be refreshed in the Knowledge Base as often as selected. This will keep Knowledge base always in sync with the latest updates on your Guru Help Center.',
        options: ['Manual', '1 day', '1 week'],
        values: ['0', '86400', '604800'],
        default: '0',
      },
      {
        name: 'shopifyProductMetafieldDefinitions',
        advanced: false,
        type: 'string',
        title: 'Select Product Metafield Definitions',
        description: 'Select Product Metafield Definitions',
        default: '',
      },
      {
        name: 'automatedUpdatesEnabled',
        advanced: false,
        type: 'boolean',
        title: 'Enable Automated Updates',
        default: false,
        description:
          'With automated updates, information from the Web sources can be refreshed in the Knowledge Base as often as needed. This will keep Knowledge base always in sync with the latest updates on your websites.',
      },
      {
        name: 'mode',
        advanced: true,
        type: 'select',
        title: 'Mode',
        options: ['embedded'],
        values: ['embedded'],
        default: 'embedded',
      },
      {
        name: 'embeddingModel',
        advanced: true,
        type: 'select',
        title: 'Embedding Model',
        options: ['text-embedding-3-large-1536'],
        values: ['text-embedding-3-large-1536'],
        default: 'text-embedding-3-large-1536',
      },
      {
        name: 'vectorStore',
        advanced: true,
        type: 'select',
        title: 'Vector Store',
        options: ['elastic'],
        values: ['elastic'],
        default: 'elastic',
      },
      {
        name: 'splitSizeThreshold',
        advanced: true,
        type: 'number',
        title: 'Document split size threshold',
        default: 2000,
        description: 'Do not split document when indexing in KB if smaller than this size (in characters).',
      },
      {
        name: 'splitOptions',
        advanced: true,
        type: 'string',
        title: 'Document split parameters',
        default: '',
        description: `Advanced document split parameters (JSON string). Keep blank for default. Example: {"chunkSize": 2000, "prependSeparators": ["\\n\\n---"]}`,
      },
      {
        name: 'semanticSplitModelName',
        advanced: true,
        type: 'string',
        title: 'Semantic split model name',
        default: DEFAULT_SEMANTIC_ANALYZER_MODEL,
        description: `Configures the LLM model of semantic splitting.`,
      },
      {
        name: 'publishMethod',
        advanced: true,
        type: 'select',
        title: 'KB document publish method',
        options: ['Manual', 'AI-Assisted', 'Automatic'],
        values: ['manual', 'ai', 'auto'],
        default: 'auto',
        description: `Defines the methods available for publishing KB documents imported from data sources:
          Manual: Each KB document imported from a data source must be manually reviewed and published.
          AI-Assisted: AI analyzes and publishes KB documents that comply with the publishing policy.
          Automatic: All KB documents are published automatically without manual intervention.`,
      },
      {
        name: 'contentPublisherModelName',
        advanced: true,
        type: 'string',
        title: 'LLM Model for AI-Assisted Publishing',
        default: DEFAULT_PUBLISHER_LLM_MODEL_NAME,
        description: `Configures the LLM model of AI-Assisted Publishing.`,
      },
      {
        name: 'publishAnalyzerInstructions',
        advanced: true,
        type: 'text',
        title: 'Instructions for AI Assisted Publishing',
        default: '',
        description: `Defines instructions for the AI to analyze and publish KB documents.`,
      },
      {
        name: 'contentFilterAI',
        advanced: true,
        type: 'text',
        title: 'AI Content Filter',
        default: '',
        description: `Describe which documents should be accepted. For example, "Content must be primarily in English language.". All documents that do not meet this requirement will be rejected. Keep blank to accept all documents.`,
      },
      {
        name: 'maxKbSearchDocuments',
        advanced: true,
        type: 'number',
        title: 'Maximum number of documents retrieved by KB search',
        default: DEFAULT_MAX_KB_SEARCH_DOCUMENTS,
        description: 'Limits the maximum number of documents retrieved by KB search.',
      },
      {
        name: 'searchByKeywordsEnabled',
        advanced: true,
        type: 'boolean',
        title: 'Enable Keyword Search',
        default: false,
        description: 'Enable document search by keywords in addition to vector search.',
      },
      {
        name: 'purgeDeletedDocumentsEnabled',
        advanced: true,
        type: 'boolean',
        title: 'Purge KB documents deleted from data source',
        default: false,
        description: 'Purge KB documents deleted from the data source.',
      },
      {
        name: 'extractTextFromImages',
        advanced: true,
        type: 'boolean',
        title: 'Extract text from images',
        default: false,
        description: 'Extract text from images',
      },
      {
        name: 'purgeDeletedEnabledWarning',
        advanced: true,
        type: 'separator',
        hint: 'WARNING: Enabling purge will delete KB documents no longer present in data source.',
      },
      //
      // +++
    ]);
    this.propsByName = {};
    this.props.map((p) => {
      this.propsByName[p.name] = p;
    });
  }
}

// All known KB Type Defs by type
export const KB_TYPE_DEFS = {
  standard: new KbTypeStandard(),
};

// Meta-information about all known KB Types
export const KB_TYPE_META = Object.values(KB_TYPE_DEFS).map((x) => {
  return {
    type: x.type,
    title: x.title,
    description: x.description,
    helpUrl: x.helpUrl,
    icon: x.icon,
  };
});

export function getKbTypeDef(kbType) {
  return kbType in KB_TYPE_DEFS ? KB_TYPE_DEFS[kbType] : emptyKbType;
}
